import { useCallback } from 'react';

import { useShowCause } from '@givebacks/api/core';
import { useCreateNote } from '@givebacks/api/store';
import { ButtonMantine, Flex, Modal, ModalProps, Textarea, notifications } from 'ui';
import { isNotEmpty, useForm } from 'ui/Form';

const NewOrderNoteModalContent = ({
  causeId,
  cartId,
  onClose,
  onSave,
}: {
  causeId: string;
  cartId: string;
  onClose: () => void;
  onSave: () => void;
}) => {
  const form = useForm({
    initialValues: {
      text: '',
    },
    validate: {
      text: isNotEmpty('Order Notes must exist'),
    },
  });

  const { data: cause } = useShowCause(causeId);
  const { mutate: createNote } = useCreateNote();

  const saveNote = useCallback(() => {
    if (form.isValid()) {
      createNote(
        {
          cartId: cartId,
          causeId: cause?.uuid,
          note: form.values.text,
        },
        {
          onSuccess: () => {
            onSave();
          },
          onError: () => {
            notifications.show({ variant: 'error', message: 'Error saving note' });
          },
        }
      );
    }
  }, [form, createNote, cartId, cause?.uuid, onSave]);

  return (
    <Flex gap="lg" direction="column">
      <Textarea
        label="Order Notes"
        placeholder="Enter order notes"
        size="md"
        {...form.getInputProps('text')}
        autosize
      />
      <Flex justify="flex-end">
        <ButtonMantine variant="subtle" onClick={onClose}>
          Cancel
        </ButtonMantine>
        <ButtonMantine onClick={saveNote}>Save</ButtonMantine>
      </Flex>
    </Flex>
  );
};

export const NewOrderNoteModal = ({
  causeId,
  cartId,
  onClose,
  onSave,
  ...rest
}: {
  causeId: string;
  cartId: string;
  onClose: () => void;
  onSave: () => void;
  opened: boolean;
} & ModalProps) => {
  return (
    <Modal title="Add Order Notes" onClose={onClose} {...rest}>
      <NewOrderNoteModalContent
        causeId={causeId}
        cartId={cartId}
        onClose={onClose}
        onSave={onSave}
      />
    </Modal>
  );
};
